import {useMemo, useState} from "react";
import {Button, Stack, Typography} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import {appStyles} from "../../../../AppStyles.ts";
import {StyledInputBase, StyledSearch, StyledSearchIconWrapper} from "../../../AppStyledComponents.tsx";
import type {AnalystInsight} from "../../../../sdo/AnalystInsight.ts";
import InsightCard from "./insights/InsightCard.tsx";

let nextId = 100;

function createEmptyInsight(): AnalystInsight {
    return {
        uid: String(nextId++),
        title: "",
        content: "",
        tags: [],
        createdBy: "CurrentUser",
        createdAt: Date.now(),
        modifiedAt: 0,
        version: 1,
    };
}

// TODO: Replace with API call to load insights from IntelProfileManager.readInsights()
function buildTestData(): AnalystInsight[] {
    return [
        {
            uid: "1",
            title: "Pattern shift in March observation window",
            content: "Subject's movement pattern shifted noticeably during the second week of March. Previous baseline showed weekday-only activity centered around the financial district; the new pattern includes weekend activity and a wider geographic spread including the warehouse district. Recommend cross-referencing with the partner watchlist updates from the same period.",
            tags: ["movement", "march-2026", "high-priority"],
            createdBy: "j.doe",
            createdAt: new Date("2026-03-18T10:24:00").getTime(),
            modifiedAt: new Date("2026-03-19T08:11:00").getTime(),
            version: 2,
        },
        {
            uid: "2",
            title: "Possible alias confirmation",
            content: "The name variant identified in the resolver hit appears to match an alias used in the 2024 case file. Need to verify with the legacy records team before promoting this insight.",
            tags: ["alias", "verification-pending"],
            createdBy: "m.chen",
            createdAt: new Date("2026-03-22T14:50:00").getTime(),
            modifiedAt: 0,
            version: 1,
        },
        {
            uid: "3",
            title: "Quick note",
            content: "Flag for review at next standup.",
            tags: [],
            createdBy: "a.patel",
            createdAt: new Date("2026-04-01T09:05:00").getTime(),
            modifiedAt: 0,
            version: 1,
        },
        {
            uid: "4",
            title: "Long-form context for downstream analysts",
            content: "This entry collects the broader context that doesn't fit into the structured fields elsewhere. The subject was first surfaced through a partner feed in late February. Initial enrichment pulled in geolocation data that placed three of the four base records within a 2km radius. The fourth record is an outlier and is currently being verified. Tag this entry as a candidate for promotion to the case briefing once the outlier is resolved.",
            tags: ["context", "promotion-candidate", "feed-partner-a"],
            createdBy: "j.doe",
            createdAt: new Date("2026-04-03T16:40:00").getTime(),
            modifiedAt: 0,
            version: 1,
        },
    ];
}

function UXIntelAnalystPanel() {
    const [insights, setInsights] = useState<AnalystInsight[]>(buildTestData);
    const [filterText, setFilterText] = useState("");
    const [newInsightUid, setNewInsightUid] = useState<string | null>(null);

    const filtered = useMemo(() => {
        if (!filterText.trim()) return insights;
        const q = filterText.toLowerCase();
        return insights.filter((i) => JSON.stringify(i).toLowerCase().includes(q));
    }, [insights, filterText]);

    // TODO: Replace with API call to IntelProfileManager.deleteInsight()
    const handleDelete = (uid: string) => {
        setInsights((prev) => prev.filter((i) => i.uid !== uid));
        if (newInsightUid === uid) setNewInsightUid(null);
    };

    // TODO: Replace with API call to IntelProfileManager.saveInsight()
    const handleUpdate = (updated: AnalystInsight) => {
        setInsights((prev) => prev.map((i) => i.uid === updated.uid ? updated : i));
    };

    const handleCreate = () => {
        const newInsight = createEmptyInsight();
        setInsights((prev) => [newInsight, ...prev]);
        setNewInsightUid(newInsight.uid);
    };

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            {/* Title */}
            <Stack direction="row" padding={1} justifyContent="center">
                <Typography sx={appStyles.panelSectionTitle}>Analyst Insights</Typography>
            </Stack>

            {/* Filter bar */}
            <Stack direction="row" spacing={1} padding={1} alignItems="center" justifyContent="center" sx={{position: "relative"}}>
                <StyledSearch>
                    <StyledSearchIconWrapper>
                        <SearchIcon sx={{fontSize: 16, color: "#7093a0"}}/>
                    </StyledSearchIconWrapper>
                    <StyledInputBase
                        placeholder="Filter insights..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </StyledSearch>

                <Typography sx={appStyles.panelFilterCount}>
                    Showing {filtered.length} of {insights.length}
                </Typography>

                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon/>}
                    onClick={handleCreate}
                    sx={{...appStyles.panelActionButton, position: "absolute", right: 8}}
                >
                    Create Insight
                </Button>
            </Stack>

            {/* Insight cards list */}
            <Stack direction="column" spacing={1} sx={{...appStyles.defaultPanelCardsWrapper, padding: "8px", alignItems: "stretch"}}>
                {filtered.map((insight) => (
                    <InsightCard
                        key={insight.uid}
                        insight={insight}
                        onDelete={handleDelete}
                        onUpdate={handleUpdate}
                        startInEditMode={insight.uid === newInsightUid}
                    />
                ))}
                {filtered.length === 0 && (
                    <Typography sx={{
                        color: "rgba(242, 245, 250, 0.4)",
                        fontSize: "0.80rem",
                        textAlign: "center",
                        padding: "24px",
                    }}>
                        No analyst insights match the current filter.
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}

export default UXIntelAnalystPanel;
